import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook to manage Echo channel subscriptions.
 * It handles subscribing to a private channel, listening for a specific event,
 * and cleaning up the subscription when the component unmounts or dependencies change.
 *
 * @param {object} echo - The Echo instance (e.g., from useEcho()).
 * @param {string|null} channelName - The full name of the channel to subscribe to (e.g., 'user.123', 'order.xyz').
 * Pass null or an empty string to skip subscription.
 * @param {string} eventName - The name of the event to listen for on the channel.
 * @param {function} callback - The callback function to execute when the event is received.
 * @returns {object|null} The current channel instance, or null if not yet subscribed.
 */
const useEchoChannel = (echo, channelName, eventName, callback) => { // channelName is now directly passed
  // useRef to hold the channel instance across re-renders
  const channelRef = useRef(null);

  // useCallback to memoize the callback to prevent unnecessary re-subscriptions
  // if the callback is defined inline in the consuming component.
  const memoizedCallback = useCallback(callback, [callback]);

  useEffect(() => {
    // Check if Echo instance or a valid channelName are not available yet
    if (!echo || !channelName || typeof channelName !== 'string') {
      console.log("useEchoChannel: Echo instance or valid channelName not ready for subscription.");
      // Ensure any existing channel is unsubscribed if parameters become invalid
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
      return;
    }

    try {
      // Clean up any previously active channel subscription before creating a new one.
      // This is crucial to prevent memory leaks and duplicate listeners on re-renders.
      if (channelRef.current) {
        console.log(`useEchoChannel: Unsubscribing from previous channel: ${channelRef.current.name}`);
        channelRef.current.unsubscribe();
        channelRef.current = null; // Clear the ref
      }

      console.log(`useEchoChannel: Subscribing to private channel: ${channelName}`);
      const channel = echo.private(channelName);

      // Listen for the specified event and attach the provided callback
      channel.listen(eventName, memoizedCallback);

      // Store the active channel instance in the ref
      channelRef.current = channel;

    } catch (error) {
      console.error("useEchoChannel: Error during channel subscription:", error);
    }

    // Cleanup function: This runs when the component unmounts or when
    // any of the effect's dependencies change (before the new effect runs).
    return () => {
      if (channelRef.current) {
        console.log(`useEchoChannel: Unsubscribing from channel on cleanup: ${channelRef.current.name}`);
        channelRef.current.unsubscribe();
        channelRef.current = null; // Clear the ref
      }
    };
  }, [echo, channelName, eventName, memoizedCallback]); // Dependencies array now includes channelName

  // Return the channel instance, though for this specific use case, it might not be strictly needed by the consumer.
  return channelRef.current;
};

export default useEchoChannel;
